import type { Table } from 'dexie';
import { db } from './index.js';
import type { LocalRoutine } from './index.js';

type TableName = 'tabs' | 'routines' | 'playlists' | 'userFavorites' | 'settings';
export type TableGetter = (name: TableName) => Table<any, any>;

/**
 * FRE-142: points every local reference to `oldId` at `newId` (routine blocks, playlists, favorites,
 * last-opened settings). Call inside a rw transaction that covers those 5 tables. Does not move the tab row.
 */
export async function rekeyTabReferencesIn(t: TableGetter, oldId: string, newId: string): Promise<void> {
  const now = new Date().toISOString();

  const routines: LocalRoutine[] = await t('routines').toArray();
  for (const r of routines) {
    let changed = false;
    const blocks = (r.blocks ?? []).map((b) => {
      let nb = b;
      if (b.tabId === oldId) {
        nb = { ...nb, tabId: newId };
        changed = true;
      }
      if (b.tab?.id === oldId) {
        nb = { ...nb, tab: { ...b.tab, id: newId } };
        changed = true;
      }
      return nb;
    });
    if (changed) await t('routines').put({ ...r, blocks, dirty: 1, updatedAt: now });
  }

  const playlists = await t('playlists').toArray();
  for (const p of playlists) {
    if (Array.isArray(p.tabIds) && p.tabIds.includes(oldId)) {
      await t('playlists').put({ ...p, tabIds: p.tabIds.map((x: string) => (x === oldId ? newId : x)), dirty: 1, updatedAt: now });
    }
  }

  const favs = (await t('userFavorites').toArray()).filter((f) => f.tabId === oldId);
  for (const f of favs) {
    await t('userFavorites').delete([f.ownerId, oldId]);
    await t('userFavorites').put({ ...f, tabId: newId, dirty: 1, updatedAt: now });
  }

  const settings = await t('settings').toArray();
  for (const s of settings) {
    if (typeof s.key === 'string' && s.key.startsWith('last_opened_tab:') && s.value?.tabId === oldId) {
      await t('settings').put({ ...s, value: { ...s.value, tabId: newId } });
    }
  }
}

/** Runtime variant: moves the tabs row too (keeps all fields), in one rw transaction. */
export async function rekeyLocalTab(oldId: string, newId: string): Promise<void> {
  if (oldId === newId) return;
  await db.transaction('rw', [db.tabs, db.routines, db.playlists, db.userFavorites, db.settings], async () => {
    const row = await db.tabs.get(oldId);
    if (row) {
      await db.tabs.delete(oldId);
      await db.tabs.put({ ...row, id: newId });
    }
    await rekeyTabReferencesIn((name) => db.table(name), oldId, newId);
  });
}
